import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@clerk/clerk-react';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { useLocalization, type Language } from '../../../../config/localization';
import Icon, { ICONS } from '../../../ui/icons';
import AudioWaveformPlaceholder from '../../../ui/AudioWaveformPlaceholder';
import Model3DPlaceholder from '../../../ui/displays/Model3DPlaceholder';
import HybridDropdown from '../../dropdowns/HybridDropdown';
import { getThumbnailUrl } from '../../../../utils/imageProxy';
import {
  hasVideoMetadataConstraints,
  validateVideoSlotConstraints,
  type MeasuredVideo,
  type VideoSlotConstraints,
} from '../../../../utils/slotVideoConstraints';
import {
  resolveGalleryMedia,
  type GallerySelectableMediaType,
} from '../../../../utils/galleryMedia';
import type { GalleryImage, GalleryFilters } from '../../../pages/gallery/GalleryContent';

interface Collection {
  id: number;
  name: string;
  description?: string;
  image_count?: number;
}

interface User {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface Workflow {
  id: number;
  name: string;
  visual_id: string;
}

interface MediaConstraints extends VideoSlotConstraints {
  audioMaxDuration?: number;  // seconds
}

interface GallerySelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (items: GalleryImage[]) => void;
  language: Language;
  maxSelection?: number;
  allowedMediaTypes?: GallerySelectableMediaType[];
  acceptedModelFormats?: readonly string[];
  initialSelectedIds?: number[];
  mediaConstraints?: MediaConstraints;
}

// Parse prompt from raw_input (mirrors GalleryImageCard logic)
function getPromptFromRawInput(rawInput?: string): string {
  if (!rawInput) return '';
  try {
    const input = JSON.parse(rawInput);
    if (input.prompt) return input.prompt;
    if (input.text) return input.text;
    if (input.inputs?.prompt) return input.inputs.prompt;
    if (input.inputs?.text) return input.inputs.text;
    if (typeof input === 'string') return input;
    return '';
  } catch {
    return '';
  }
}

function getAudioDurationFromRawInput(rawInput?: string): number | null {
  if (!rawInput) return null;
  try {
    const input = JSON.parse(rawInput);
    if (input.audio_duration) return input.audio_duration;
    return null;
  } catch {
    return null;
  }
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const DEFAULT_ALLOWED_MEDIA_TYPES: GallerySelectableMediaType[] = ['image', 'video', 'audio'];

const getVideoMetadataKey = (imageId: number, sourceUrl: string): string =>
  `${imageId}:${sourceUrl}`;

export const VIDEO_METADATA_PROBE_TIMEOUT_MS = 15_000;

interface VideoMetadataProbeProps {
  metadataKey: string;
  url: string;
  onLoadedMetadata: (metadataKey: string, video: HTMLVideoElement) => void;
  onError: (metadataKey: string) => void;
  className?: string;
  loop?: boolean;
  isPreviewing?: boolean;
}

function VideoMetadataProbe({
  metadataKey,
  url,
  onLoadedMetadata,
  onError,
  className,
  loop,
  isPreviewing,
}: VideoMetadataProbeProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const settledRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    settledRef.current = false;
    timeoutRef.current = setTimeout(() => {
      if (settledRef.current) return;
      settledRef.current = true;
      onError(metadataKey);
    }, VIDEO_METADATA_PROBE_TIMEOUT_MS);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [metadataKey, onError, url]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || isPreviewing === undefined) return;
    if (isPreviewing) {
      video.currentTime = 0;
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isPreviewing]);

  const settle = (callback: () => void) => {
    if (settledRef.current) return;
    settledRef.current = true;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    callback();
  };

  return (
    <video
      ref={videoRef}
      src={url}
      className={className}
      preload="metadata"
      muted
      loop={loop}
      playsInline
      onLoadedMetadata={(event) => settle(() => onLoadedMetadata(metadataKey, event.currentTarget))}
      onError={() => settle(() => onError(metadataKey))}
    />
  );
}

interface LazyVideoMetadataProbeProps extends VideoMetadataProbeProps {
  intersectionRootRef: React.RefObject<HTMLElement>;
}

function LazyVideoMetadataProbe({
  metadataKey,
  url,
  onLoadedMetadata,
  onError,
  intersectionRootRef,
}: LazyVideoMetadataProbeProps) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setShouldLoad(true);
      observer.disconnect();
    }, { root: intersectionRootRef.current, rootMargin: '200px', threshold: 0 });
    observer.observe(host);
    return () => observer.disconnect();
  }, [intersectionRootRef, metadataKey, onError, onLoadedMetadata, url]);

  return (
    <span
      ref={hostRef}
      data-video-metadata-probe="true"
      className="absolute inset-0 pointer-events-none opacity-0"
      aria-hidden="true"
    >
      {shouldLoad && (
        <VideoMetadataProbe
          metadataKey={metadataKey}
          url={url}
          onLoadedMetadata={onLoadedMetadata}
          onError={onError}
        />
      )}
    </span>
  );
}

export default function GallerySelectionModal({
  isOpen,
  onClose,
  onSelect,
  language,
  maxSelection = 12,
  allowedMediaTypes = DEFAULT_ALLOWED_MEDIA_TYPES,
  acceptedModelFormats,
  initialSelectedIds = [],
  mediaConstraints
}: GallerySelectionModalProps) {
  const t = useLocalization(language);
  const { getToken } = useAuth();
  const { currentOrganization } = useOrganization();

  // State
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [selectedItems, setSelectedItems] = useState<Map<number, GalleryImage>>(new Map());
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filters, setFilters] = useState<GalleryFilters>({
    limit: 50,
    offset: 0
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Filter options state
  const [collections, setCollections] = useState<Collection[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  // Infinite scroll refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const cooldownRef = useRef(false);
  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  // 🔴 Server-side cursor — NOT `images.length`. Rows are dropped client-side
  // (3D models, URL-less rows), so the displayed count trails what the server
  // has already returned. Paging on the filtered count refetches rows already on
  // screen (duplicate `key={image.id}`), and a page that is entirely filtered out
  // never advances the offset at all while `hasMore` stays true — which the
  // auto-load effect below turns into an unbounded refetch loop.
  const fetchedCountRef = useRef(0);

  // Track whether selection restoration is in progress (prevents premature confirm)
  const [isRestoringSelection, setIsRestoringSelection] = useState(false);

  // Video metadata used by duration and resolution constraint checking.
  const [videoMetadata, setVideoMetadata] = useState<Map<string, MeasuredVideo>>(new Map());
  const [videoMetadataStatus, setVideoMetadataStatus] = useState<Map<string, 'loaded' | 'failed'>>(new Map());

  // Hold-to-preview state
  const [previewingId, setPreviewingId] = useState<number | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const isHoldingRef = useRef(false);

  const startHoldPreview = useCallback((image: GalleryImage, mediaType: GallerySelectableMediaType) => {
    if (mediaType === 'image' || mediaType === 'model') return;
    isHoldingRef.current = false;
    holdTimerRef.current = setTimeout(() => {
      isHoldingRef.current = true;
      setPreviewingId(image.id);
      const url = resolveGalleryMedia(image, acceptedModelFormats).url;
      if (mediaType === 'audio' && url) {
        const audio = new Audio(url);
        audio.play().catch(() => {});
        previewAudioRef.current = audio;
      }
    }, 500);
  }, [acceptedModelFormats]);

  const stopHoldPreview = useCallback(() => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = undefined;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewingId(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
        previewAudioRef.current = null;
      }
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    };
  }, []);

  // AbortController for cancelling stale fetch requests
  const fetchAbortControllerRef = useRef<AbortController | null>(null);

  // AbortController for cancelling stale selected-items fetch requests
  const selectedItemsAbortControllerRef = useRef<AbortController | null>(null);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async (orgId: number) => {
    setFiltersLoading(true);
    try {
      const token = await getToken({ template: 'withmail' });

      const [collectionsRes, usersRes, workflowsRes] = await Promise.all([
        fetch(`/api/organization/collections?organizationId=${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/organization/users?organizationId=${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/organization/workflows?organizationId=${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (collectionsRes.ok) {
        const data = await collectionsRes.json();
        setCollections(data.collections || []);
      }

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }

      if (workflowsRes.ok) {
        const data = await workflowsRes.json();
        setWorkflows(data.workflows || []);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setFiltersLoading(false);
    }
  }, [getToken]);

  // Fetch images
  const fetchImages = useCallback(async (newFilters: GalleryFilters, append = false) => {
    if (!currentOrganization?.id) return;

    // Fresh query (search / filter change / reopen): restart the server cursor so
    // a failed or aborted request cannot leave a stale offset behind.
    if (!append) fetchedCountRef.current = 0;

    // Cancel any in-flight request to prevent stale responses
    if (fetchAbortControllerRef.current) {
      fetchAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    fetchAbortControllerRef.current = abortController;

    setLoading(true);

    try {
      const token = await getToken({ template: 'withmail' });

      const params = new URLSearchParams({
        organizationId: currentOrganization.id.toString(),
        limit: newFilters.limit.toString(),
        offset: newFilters.offset.toString()
      });

      if (newFilters.search) params.set('search', newFilters.search);

      // Add filter parameters
      if (newFilters.collectionIds && newFilters.collectionIds.length > 0) {
        newFilters.collectionIds.forEach(id => params.append('collectionId', id.toString()));
      }
      if (newFilters.userIds && newFilters.userIds.length > 0) {
        newFilters.userIds.forEach(id => params.append('userId', id));
      }
      if (newFilters.workflowIds && newFilters.workflowIds.length > 0) {
        newFilters.workflowIds.forEach(id => params.append('workflowId', id.toString()));
      }
      if (newFilters.dateFrom) params.set('dateFrom', newFilters.dateFrom);
      if (newFilters.dateTo) params.set('dateTo', newFilters.dateTo);

      // The legacy default allows all three raster/time-based types, so it can
      // keep the unfiltered request, as does an explicit all-four picker. A
      // model-specific picker sends a filter; the gallery endpoint deliberately
      // accepts the simple DB value `3d`.
      const includesEveryLegacyType = DEFAULT_ALLOWED_MEDIA_TYPES.every(type =>
        allowedMediaTypes.includes(type)
      );
      const isUnfiltered = includesEveryLegacyType &&
        allowedMediaTypes.length === (allowedMediaTypes.includes('model') ? 4 : 3);
      if (!isUnfiltered) {
        const contentTypes: string[] = [];
        if (allowedMediaTypes.includes('image')) {
          contentTypes.push('image/jpeg', 'image/png', 'image/gif', 'image/webp');
        }
        if (allowedMediaTypes.includes('video')) {
          contentTypes.push('video/mp4', 'video/webm', 'video/quicktime');
        }
        if (allowedMediaTypes.includes('audio')) {
          contentTypes.push('audio/mpeg', 'audio/wav', 'audio/ogg');
        }
        if (allowedMediaTypes.includes('model')) {
          contentTypes.push('3d');
        }
        contentTypes.forEach(type => params.append('contentType', type));
      }

      const response = await fetch(`/api/organization/collections/gallery?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortController.signal
      });

      if (response.ok) {
        const data = await response.json();
        const fetchedImages = data.images || [];

        // The server filter narrows the result set, while this client guard also
        // handles stale/missing content types and the intentionally unfiltered
        // legacy request. Model detection uses the original URL before a poster.
        const validImages = fetchedImages.flatMap((img: GalleryImage) => {
          if (!(img.preview_urls || img.original_urls || img.external_urls)) return [];
          const resolved = resolveGalleryMedia(img, acceptedModelFormats);
          return resolved.url && allowedMediaTypes.includes(resolved.mediaType)
            ? [{ ...img, mediaType: resolved.mediaType, resolvedUrl: resolved.url }]
            : [];
        });

        setImages(prev => append ? [...prev, ...validImages] : validImages);
        // Advance the cursor by the RAW page size — `hasMore` is derived from the
        // same raw count, so both must ignore the client-side filtering above.
        fetchedCountRef.current = newFilters.offset + fetchedImages.length;
        setHasMore(fetchedImages.length === newFilters.limit);
      } else {
        // 🔴 Stop paging on a failed page. Neither the cursor nor `hasMore` moved,
        // and the auto-load effect below re-fires on every loading false→true→false
        // cycle — leaving `hasMore` true would refetch the same offset forever
        // against an endpoint that is already erroring. A filter/search change
        // resets the cursor and lets the user retry.
        setHasMore(false);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Failed to fetch gallery images:', error);
      setHasMore(false); // same reason as the !response.ok branch above
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, [currentOrganization, getToken, allowedMediaTypes, acceptedModelFormats]);

  // Fetch selected items by IDs (for restoring selection on reopen)
  const fetchSelectedItemsByIds = useCallback(async (itemIds: number[]) => {
    if (!currentOrganization?.id || itemIds.length === 0) return;

    // Abort any previous selected-items fetch
    if (selectedItemsAbortControllerRef.current) {
      selectedItemsAbortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    selectedItemsAbortControllerRef.current = abortController;

    setIsRestoringSelection(true);

    try {
      const token = await getToken({ template: 'withmail' });
      const params = new URLSearchParams({
        organizationId: currentOrganization.id.toString(),
        limit: itemIds.length.toString(),
        offset: '0'
      });
      itemIds.forEach(id => params.append('id', id.toString()));

      // Do NOT apply content type filter here — pre-selected items must be
      // restored regardless of current allowedMediaTypes (which may have
      // changed since the items were originally selected).

      const response = await fetch(`/api/organization/collections/gallery?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortController.signal
      });

      if (abortController.signal.aborted) return;

      if (response.ok) {
        const data = await response.json();
        const fetchedImages: GalleryImage[] = data.images || [];
        setSelectedItems(prev => {
          const newMap = new Map(prev);
          for (const img of fetchedImages) {
            if (newMap.size >= maxSelection) break;
            const resolved = resolveGalleryMedia(img, acceptedModelFormats);
            if (!resolved.url || !allowedMediaTypes.includes(resolved.mediaType)) continue;
            newMap.set(img.id, {
              ...img,
              mediaType: resolved.mediaType,
              resolvedUrl: resolved.url,
            });
          }
          return newMap;
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('Failed to fetch selected items:', error);
    } finally {
      if (!abortController.signal.aborted) {
        setIsRestoringSelection(false);
      }
    }
  }, [currentOrganization, getToken, maxSelection, allowedMediaTypes, acceptedModelFormats]);

  // Initial load - only run when modal opens
  useEffect(() => {
    if (isOpen && currentOrganization?.id) {
      setImages([]);
      setHasMore(true);
      const initialFilters = { limit: 50, offset: 0 };
      setFilters(initialFilters);
      setSearchQuery('');
      setVideoMetadata(new Map());
      setVideoMetadataStatus(new Map());
      fetchImages(initialFilters);
      fetchFilterOptions(currentOrganization.id);

      // Reset restore flag (may have been left true if previous restore was aborted)
      setIsRestoringSelection(false);
      // Restore selection from initialSelectedIds
      setSelectedItems(new Map());
      if (initialSelectedIds.length > 0) {
        fetchSelectedItemsByIds(initialSelectedIds);
      }
    }

    return () => {
      // Cancel any in-flight requests when modal closes
      if (fetchAbortControllerRef.current) {
        fetchAbortControllerRef.current.abort();
        fetchAbortControllerRef.current = null;
      }
      if (selectedItemsAbortControllerRef.current) {
        selectedItemsAbortControllerRef.current.abort();
        selectedItemsAbortControllerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentOrganization?.id]);

  // Handle search
  const handleSearch = useCallback(() => {
    const newFilters = { ...filters, search: searchQuery || undefined, offset: 0 };
    setFilters(newFilters);
    setImages([]);
    setHasMore(true);
    fetchImages(newFilters);
  }, [filters, searchQuery, fetchImages]);

  // Handle filter change
  const handleFilterChange = useCallback((key: keyof GalleryFilters, value: any) => {
    const newFilters = { ...filters, [key]: value, offset: 0 };
    setFilters(newFilters);
    setImages([]);
    setHasMore(true);
    fetchImages(newFilters);
  }, [filters, fetchImages]);

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    const newFilters: GalleryFilters = { limit: 50, offset: 0 };
    setFilters(newFilters);
    setSearchQuery('');
    setImages([]);
    setHasMore(true);
    fetchImages(newFilters);
  }, [fetchImages]);

  // Count active filters
  const activeFiltersCount = (
    (filters.collectionIds?.length || 0) +
    (filters.userIds?.length || 0) +
    (filters.workflowIds?.length || 0) +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0)
  );

  // Handle load more
  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const newFilters = { ...filters, offset: fetchedCountRef.current };
    setFilters(newFilters);
    fetchImages(newFilters, true);
  }, [filters, loading, hasMore, fetchImages]);

  // Stable ref for handleLoadMore to avoid re-creating the observer
  const onLoadMoreRef = useRef(handleLoadMore);
  onLoadMoreRef.current = handleLoadMore;

  // IntersectionObserver-based infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;

    const root = scrollContainerRef.current || null;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadingRef.current && !cooldownRef.current) {
          onLoadMoreRef.current();
          cooldownRef.current = true;
          cooldownTimerRef.current = setTimeout(() => {
            cooldownRef.current = false;
          }, 1000);
        }
      },
      { root, rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (cooldownTimerRef.current) clearTimeout(cooldownTimerRef.current);
      cooldownRef.current = false;
    };
  }, [hasMore, isOpen]);

  // Auto-load more if sentinel is still visible after a batch completes
  // (handles case when initial batch doesn't fill the scroll container)
  // No cooldown check here — one run per batch: it re-fires on the loading
  // false→true→false cycle, and `handleLoadMore` pages off the raw server cursor,
  // so every run advances the offset even when a whole batch was filtered out.
  // A page that fails (non-ok / network) advances nothing, so `fetchImages`
  // clears `hasMore` in that case — otherwise this effect would loop on it.
  useEffect(() => {
    if (loading || !hasMore || !isOpen) return;

    const sentinel = sentinelRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!sentinel || !scrollContainer) return;

    const sentinelRect = sentinel.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();
    const isVisible = sentinelRect.top < containerRect.bottom + 200;

    if (isVisible) {
      onLoadMoreRef.current();
    }
  }, [loading, hasMore, isOpen, images.length]);

  // Toggle selection
  // Check if a gallery item is ineligible based on media constraints
  const getIneligibleReason = useCallback((image: GalleryImage, mediaType: GallerySelectableMediaType): string | null => {
    if (!mediaConstraints) return null;

    if (mediaType === 'audio' && mediaConstraints.audioMaxDuration) {
      const duration = getAudioDurationFromRawInput(image.raw_input);
      if (duration != null && duration > mediaConstraints.audioMaxDuration) {
        return `${t.widgets?.inputMedia?.durationTooLong || 'Duration too long'} (${formatDuration(duration)} > max ${formatDuration(mediaConstraints.audioMaxDuration)})`;
      }
    }

    if (mediaType === 'video') {
      if (!hasVideoMetadataConstraints(mediaConstraints)) return null;
      const { url } = resolveGalleryMedia(image, acceptedModelFormats);
      const metadataKey = getVideoMetadataKey(image.id, url);
      const status = videoMetadataStatus.get(metadataKey);
      if (!status) return t.widgets?.inputMedia?.videoMetadataLoading || 'Loading video metadata';
      if (status === 'failed') return null;
      return validateVideoSlotConstraints(
        videoMetadata.get(metadataKey) ?? {},
        mediaConstraints,
        t.widgets?.inputMedia,
      );
    }

    return null;
  }, [acceptedModelFormats, mediaConstraints, t, videoMetadata, videoMetadataStatus]);

  const isSelectionBlocked = useCallback((image: GalleryImage): boolean => {
    const resolved = resolveGalleryMedia(image, acceptedModelFormats);
    const mediaType = image.mediaType || resolved.mediaType;
    return getIneligibleReason(image, mediaType) !== null;
  }, [acceptedModelFormats, getIneligibleReason]);

  const hasBlockedSelection = Array.from(selectedItems.values()).some(isSelectionBlocked);

  const handleVideoMetadataLoaded = useCallback((metadataKey: string, video: HTMLVideoElement) => {
    const metadata: MeasuredVideo = {
      ...(video.videoWidth > 0 ? { width: video.videoWidth } : {}),
      ...(video.videoHeight > 0 ? { height: video.videoHeight } : {}),
      ...(Number.isFinite(video.duration) ? { duration: video.duration } : {}),
    };
    setVideoMetadata(prev => {
      const next = new Map(prev);
      next.set(metadataKey, metadata);
      return next;
    });
    setVideoMetadataStatus(prev => {
      const next = new Map(prev);
      next.set(metadataKey, 'loaded');
      return next;
    });
  }, []);

  const handleVideoMetadataFailed = useCallback((metadataKey: string) => {
    setVideoMetadataStatus(prev => {
      const next = new Map(prev);
      next.set(metadataKey, 'failed');
      return next;
    });
  }, []);

  // Metadata may arrive before an async selection restore finishes. Reconcile on
  // both state changes so an invalid restored item cannot remain selected solely
  // because the card's one-shot metadata event ran first. Non-video constraints
  // (currently audio duration) are known synchronously and must be reconciled too:
  // a restored off-page item otherwise has no card through which it can be removed.
  useEffect(() => {
    if (!mediaConstraints) return;

    const invalidIds = Array.from(selectedItems.values()).flatMap(item => {
      const resolved = resolveGalleryMedia(item, acceptedModelFormats);
      const mediaType = item.mediaType || resolved.mediaType;
      if (mediaType === 'video') {
        if (!hasVideoMetadataConstraints(mediaConstraints)) return [];
        const metadataKey = getVideoMetadataKey(item.id, resolved.url);
        if (videoMetadataStatus.get(metadataKey) !== 'loaded') return [];
        return validateVideoSlotConstraints(
          videoMetadata.get(metadataKey) ?? {},
          mediaConstraints,
          t.widgets?.inputMedia,
        ) ? [item.id] : [];
      }
      return getIneligibleReason(item, mediaType) ? [item.id] : [];
    });

    if (invalidIds.length === 0) return;
    setSelectedItems(prev => {
      const next = new Map(prev);
      invalidIds.forEach(id => next.delete(id));
      return next;
    });
  }, [
    acceptedModelFormats,
    getIneligibleReason,
    mediaConstraints,
    selectedItems,
    t,
    videoMetadata,
    videoMetadataStatus,
  ]);

  // Restored selections are fetched separately and may not exist on the current
  // gallery page (or may be below the viewport). Probe them eagerly unless their
  // visible card already measures the submitted original, so restoring a selection
  // can never leave Confirm waiting for the user to scroll.
  const visibleImageIds = new Set(images.map(image => image.id));
  const selectedVideosRequiringProbe = hasVideoMetadataConstraints(mediaConstraints)
    ? Array.from(selectedItems.values()).flatMap(item => {
          const resolved = resolveGalleryMedia(item, acceptedModelFormats);
          const mediaType = item.mediaType || resolved.mediaType;
          const metadataKey = getVideoMetadataKey(item.id, resolved.url);
          const visibleCardMeasuresOriginal = visibleImageIds.has(item.id)
            && resolved.previewUrl === resolved.url;
          return mediaType === 'video' && resolved.url
            && !visibleCardMeasuresOriginal
            && !videoMetadataStatus.has(metadataKey)
            ? [{ metadataKey, url: resolved.url }]
            : [];
        })
    : [];

  const handleToggleSelect = useCallback((image: GalleryImage) => {
    setSelectedItems(prev => {
      const newMap = new Map(prev);
      if (newMap.has(image.id)) {
        newMap.delete(image.id);
      } else if (newMap.size < maxSelection) {
        newMap.set(image.id, image);
      }
      return newMap;
    });
  }, [maxSelection]);

  // Confirm selection
  const handleConfirm = useCallback(() => {
    const eligibleItems = Array.from(selectedItems.values()).filter(item => !isSelectionBlocked(item));
    if (eligibleItems.length === 0) return;
    onSelect(eligibleItems);
    onClose();
  }, [selectedItems, isSelectionBlocked, onSelect, onClose]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-gray-900 rounded-2xl w-[80vw] max-w-6xl h-[85vh] flex flex-col overflow-hidden border border-purple-500/30 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {selectedVideosRequiringProbe.length > 0 && (
          <div className="absolute h-px w-px overflow-hidden opacity-0 pointer-events-none" aria-hidden="true">
            {selectedVideosRequiringProbe.map(({ metadataKey, url }) => (
              <VideoMetadataProbe
                key={metadataKey}
                metadataKey={metadataKey}
                url={url}
                onLoadedMetadata={handleVideoMetadataLoaded}
                onError={handleVideoMetadataFailed}
              />
            ))}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800/50">
          <div className="flex items-center gap-3">
            <Icon name={ICONS.GALLERY} size={24} className="text-purple-400" />
            <h2 className="text-xl font-bold text-white">
              {t.widgets?.inputMedia?.gallerySelectionTitle || 'Select media from gallery'}
            </h2>
            <span
              data-testid="selection-counter"
              className={`text-sm px-2 py-0.5 rounded-full ${
                selectedItems.size >= maxSelection
                  ? 'bg-red-500/20 text-red-400'
                  : selectedItems.size > 0
                    ? 'text-purple-300'
                    : 'text-gray-400'
              }`}
            >
              {selectedItems.size >= maxSelection
                ? (t.widgets?.inputMedia?.selectionLimitReached || 'Limit reached')
                : `${selectedItems.size} / ${maxSelection} ${t.widgets?.inputMedia?.selectionCounter || 'selected'}`
              }
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <Icon name={ICONS.X} size={24} />
          </button>
        </div>

        {/* Main content - filters left, grid right */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left sidebar - Filters */}
          <div className="w-64 flex-shrink-0 border-r border-gray-700/50 bg-gray-800/30 overflow-y-auto p-4 space-y-4">
            {/* Filter header */}
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                <Icon name={ICONS.SLIDERS} size={16} />
                {t.galleryPage?.filters || 'Filters'}
                {activeFiltersCount > 0 && (
                  <span className="bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {activeFiltersCount}
                  </span>
                )}
              </h3>
              {activeFiltersCount > 0 && (
                <button
                  onClick={handleClearFilters}
                  className="text-xs text-red-400 hover:text-red-300 transition-colors"
                >
                  {t.galleryPage?.clearFilters || 'Clear'}
                </button>
              )}
            </div>

            {/* Date range */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-400">
                {t.galleryPage?.dateRange || 'Date Range'}
              </label>
              <div className="space-y-2">
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value || undefined)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:border-purple-500 [color-scheme:dark]"
                  placeholder={t.galleryPage?.dateFrom || 'From'}
                />
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value || undefined)}
                  className="w-full px-2 py-1.5 bg-gray-800 border border-gray-600 rounded text-white text-sm focus:border-purple-500 [color-scheme:dark]"
                  placeholder={t.galleryPage?.dateTo || 'To'}
                />
              </div>
            </div>

            {/* Collections */}
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-400">
                {t.galleryPage?.collections || 'Collections'}
              </label>
              <HybridDropdown
                options={collections.map(c => ({
                  id: c.id,
                  label: c.name,
                  subtitle: c.description,
                  count: c.image_count
                }))}
                selectedIds={filters.collectionIds || []}
                onSelectionChange={(ids) => handleFilterChange('collectionIds', ids.length > 0 ? ids as number[] : undefined)}
                placeholder={t.galleryPage?.selectCollections || 'Select...'}
                searchPlaceholder={t.galleryPage?.searchCollections || 'Search...'}
                selectedLabel={t.galleryPage?.filterSelected || 'Selected'}
                loading={filtersLoading}
                language={language}
                emptyMessage={t.collections?.noCollections || 'No collections'}
                allowMultiple={true}
              />
            </div>

            {/* Workflows */}
            {workflows.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-400">
                  {t.galleryPage?.workflows || 'Workflows'}
                </label>
                <HybridDropdown
                  options={workflows.map(w => ({
                    id: w.id,
                    label: w.name,
                    subtitle: w.visual_id
                  }))}
                  selectedIds={filters.workflowIds || []}
                  onSelectionChange={(ids) => handleFilterChange('workflowIds', ids.length > 0 ? ids as number[] : undefined)}
                  placeholder={t.galleryPage?.selectWorkflows || 'Select...'}
                  searchPlaceholder={t.galleryPage?.searchWorkflows || 'Search...'}
                  selectedLabel={t.galleryPage?.filterSelected || 'Selected'}
                  loading={filtersLoading}
                  language={language}
                  emptyMessage={t.galleryPage?.filterNoWorkflows || 'No workflows'}
                  allowMultiple={true}
                />
              </div>
            )}

            {/* Users */}
            {users.length > 0 && (
              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-400">
                  {t.galleryPage?.users || 'Users'}
                </label>
                <HybridDropdown
                  options={users.map(u => ({
                    id: u.user_id,
                    label: u.email,
                    subtitle: [u.first_name, u.last_name].filter(Boolean).join(' ') || undefined
                  }))}
                  selectedIds={filters.userIds || []}
                  onSelectionChange={(ids) => handleFilterChange('userIds', ids.length > 0 ? ids as string[] : undefined)}
                  placeholder={t.galleryPage?.selectUsers || 'Select...'}
                  searchPlaceholder={t.galleryPage?.searchUsers || 'Search...'}
                  selectedLabel={t.galleryPage?.filterSelected || 'Selected'}
                  loading={filtersLoading}
                  language={language}
                  emptyMessage={t.galleryPage?.filterNoUsers || 'No users'}
                  allowMultiple={true}
                />
              </div>
            )}
          </div>

          {/* Right side - Search and Grid */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Search bar */}
            <div className="p-4 border-b border-gray-700/50">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Icon
                    name={ICONS.SEARCH}
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                  />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder={t.galleryPage?.searchPlaceholder || 'Search...'}
                    className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  />
                </div>
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  {t.widgets?.inputMedia?.search || 'Search'}
                </button>
              </div>
            </div>

            {/* Image grid */}
            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4">
          {images.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <Icon name={ICONS.GALLERY} size={64} className="mb-4 opacity-50" />
              <p>{t.galleryPage?.noResults || 'No images found'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((image) => {
                const isSelected = selectedItems.has(image.id);
                const resolved = resolveGalleryMedia(image, acceptedModelFormats);
                const previewUrl = resolved.previewUrl;
                const originalUrl = resolved.url;
                const mediaType = image.mediaType || resolved.mediaType;
                const videoMetadataKey = getVideoMetadataKey(image.id, originalUrl);
                const previewIsOriginal = previewUrl === originalUrl;
                const shouldProbeOriginalVideo = mediaType === 'video'
                  && hasVideoMetadataConstraints(mediaConstraints)
                  && !!originalUrl
                  && !previewIsOriginal
                  && !isSelected
                  && !videoMetadataStatus.has(videoMetadataKey);
                const ineligibleReason = getIneligibleReason(image, mediaType);

                const isPreviewing = previewingId === image.id;

                return (
                  <div
                    key={image.id}
                    onClick={() => {
                      if (isHoldingRef.current) { isHoldingRef.current = false; return; }
                      // Loading/invalid cards cannot be added, but an already-selected
                      // restored item must always remain removable.
                      if (isSelected || !ineligibleReason) handleToggleSelect(image);
                    }}
                    onMouseDown={() => !ineligibleReason && startHoldPreview(image, mediaType)}
                    onMouseUp={stopHoldPreview}
                    onMouseLeave={stopHoldPreview}
                    onTouchStart={() => !ineligibleReason && startHoldPreview(image, mediaType)}
                    onTouchEnd={stopHoldPreview}
                    className={`
                      relative aspect-square rounded-lg overflow-hidden
                      border-2 transition-all duration-200 group select-none
                      ${isPreviewing
                        ? 'border-purple-400 shadow-lg shadow-purple-400/40 scale-[1.03]'
                        : ineligibleReason
                        ? isSelected
                          ? 'border-purple-500 opacity-75 cursor-pointer grayscale'
                          : 'border-transparent opacity-50 cursor-not-allowed grayscale'
                        : isSelected
                          ? 'border-purple-500 shadow-lg shadow-purple-500/30 scale-[1.02] cursor-pointer'
                          : 'border-transparent hover:border-gray-600 cursor-pointer'
                      }
                    `}
                  >
                    {shouldProbeOriginalVideo && (
                      <LazyVideoMetadataProbe
                        key={videoMetadataKey}
                        metadataKey={videoMetadataKey}
                        url={originalUrl}
                        onLoadedMetadata={handleVideoMetadataLoaded}
                        onError={handleVideoMetadataFailed}
                        intersectionRootRef={scrollContainerRef}
                      />
                    )}
                    {/* Thumbnail */}
                    {mediaType === 'model' ? (
                      <Model3DPlaceholder url={originalUrl} language={language} />
                    ) : mediaType === 'image' ? (
                      <img
                        src={getThumbnailUrl(previewUrl, 400)}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : mediaType === 'video' && previewIsOriginal
                      && hasVideoMetadataConstraints(mediaConstraints) ? (
                      <VideoMetadataProbe
                        metadataKey={videoMetadataKey}
                        url={previewUrl}
                        onLoadedMetadata={handleVideoMetadataLoaded}
                        onError={handleVideoMetadataFailed}
                        className="w-full h-full object-cover"
                        loop
                        isPreviewing={isPreviewing}
                      />
                    ) : mediaType === 'video' ? (
                      <video
                        ref={(el) => {
                          if (!el) return;
                          if (isPreviewing) { el.currentTime = 0; el.play().catch(() => {}); }
                          else { el.pause(); }
                        }}
                        src={previewUrl}
                        className="w-full h-full object-cover"
                        muted
                        loop
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-purple-900/40 to-gray-800 flex flex-col items-center justify-center p-3">
                        <AudioWaveformPlaceholder size="medium" />
                        {(() => {
                          const duration = getAudioDurationFromRawInput(image.raw_input);
                          const prompt = getPromptFromRawInput(image.raw_input);
                          return (
                            <>
                              {duration != null && (
                                <span className="mt-2 text-sm text-purple-300 font-mono">{formatDuration(duration)}</span>
                              )}
                              {prompt && (
                                <p className={`${duration != null ? 'mt-1' : 'mt-2'} text-sm text-gray-300 text-center line-clamp-3 leading-relaxed`}>{prompt}</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Playing indicator */}
                    {isPreviewing && (
                      <div className="absolute top-2 right-2 bg-purple-500 rounded-full px-2 py-0.5 flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        <span className="text-[10px] text-white font-medium">Playing</span>
                      </div>
                    )}

                    {/* Ineligible overlay with reason */}
                    {ineligibleReason && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center p-2">
                        <span className="text-xs text-red-300 text-center leading-snug font-medium">{ineligibleReason}</span>
                      </div>
                    )}

                    {/* Selection overlay */}
                    {(!ineligibleReason || isSelected) && (
                      <div className={`
                        absolute inset-0 bg-purple-500/20 transition-opacity
                        ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}
                      `} />
                    )}

                    {/* Checkbox */}
                    {(!ineligibleReason || isSelected) && (
                    <div className={`
                      absolute top-2 left-2 w-6 h-6 rounded border-2 flex items-center justify-center transition-all
                      ${isSelected
                        ? 'bg-purple-500 border-purple-400'
                        : 'bg-black/50 border-gray-400 group-hover:border-purple-400'
                      }
                    `}>
                      {isSelected && <Icon name={ICONS.CHECK} size={14} className="text-white" />}
                    </div>
                    )}

                    {/* Media type badge */}
                    <div className={`
                      absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase
                      ${mediaType === 'image' ? 'bg-blue-500/80 text-white' : ''}
                      ${mediaType === 'video' ? 'bg-purple-500/80 text-white' : ''}
                      ${mediaType === 'audio' ? 'bg-orange-500/80 text-white' : ''}
                      ${mediaType === 'model' ? 'bg-cyan-600/80 text-white' : ''}
                    `}>
                      {mediaType === 'image' ? 'IMG' : mediaType === 'model' ? '3D' : mediaType.slice(0, 3).toUpperCase()}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sentinel always in DOM for IntersectionObserver */}
          <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />
          {hasMore && loading && images.length > 0 && (
            <div className="mt-4 flex justify-center">
              <Icon name={ICONS.REFRESH} size={24} className="animate-spin text-purple-400" />
            </div>
          )}

          {/* Loading indicator */}
          {loading && images.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <Icon name={ICONS.REFRESH} size={32} className="animate-spin text-purple-400" />
            </div>
          )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-700 bg-gray-800/50">
          <div className="text-sm text-gray-400 flex flex-col gap-0.5">
            {selectedItems.size > 0 && (
              <span>
                {selectedItems.size} {t.widgets?.inputMedia?.selectedFromGallery || 'from gallery'}
              </span>
            )}
            {(allowedMediaTypes.includes('audio') || allowedMediaTypes.includes('video')) && (
              <span className="text-[11px] text-gray-500">
                {language === 'ru' ? '💡 Зажмите карточку для предпросмотра аудио/видео' : '💡 Hold card to preview audio/video'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              {t.common?.cancel || 'Cancel'}
            </button>
            <button
              onClick={handleConfirm}
              disabled={selectedItems.size === 0 || isRestoringSelection || hasBlockedSelection}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {t.widgets?.inputMedia?.confirmSelection || 'Confirm'} ({selectedItems.size})
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
